/**
 * Códigos: la bandeja de solicitudes de transferencia.
 *
 * Cuando alguien pide el código de autorización de su dominio, su solicitud cae
 * aquí. No hay nada que automatizar: el código lo saca una persona del panel del
 * registrador, porque ninguna API lo expone —un código filtrado es el secuestro
 * del dominio—. Lo que esta pantalla hace es que ese trabajo manual tenga un
 * sitio con fecha, en vez de una carpeta de correo donde se pierde.
 *
 * Marcar «enviado» no manda nada: cierra la solicitud. Y eso es lo que apaga el
 * «solicitado» en la ventana del cliente, así que no es un apunte para nosotros
 * — es la respuesta que él está esperando.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconCheck, IconClipboard, IconInbox, IconLoader2, IconMail, IconSend, IconWorld } from '@tabler/icons-react'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { deliverAuthCode, listAuthCodeRequests, type AdminAuthCodeRequest } from '../../lib/api'
import { translate, useI18n, translateServerError } from '../../lib/i18n'

type Filter = 'pending' | 'sent' | 'all'

/* En claves: es una constante de módulo y no puede llamar a un hook. */
const FILTERS: { id: Filter; labelKey: string }[] = [
  { id: 'pending', labelKey: 'authCodes.filterPending' },
  { id: 'sent', labelKey: 'authCodes.filterSent' },
  { id: 'all', labelKey: 'authCodes.filterAll' },
]

/** «hace 3 horas» / «hace 2 días». Lo que importa aquí es cuánto lleva esperando. */
function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
  if (minutes < 60) return minutes <= 1 ? translate('authCodes.agoNow') : translate('authCodes.agoMinutes', { count: minutes })
  const hours = Math.round(minutes / 60)
  if (hours < 24) return translate(hours === 1 ? 'authCodes.agoHourOne' : 'authCodes.agoHours', { count: hours })
  const days = Math.round(hours / 24)
  return translate(days === 1 ? 'authCodes.agoDayOne' : 'authCodes.agoDays', { count: days })
}

/**
 * Si se está pasando el plazo que le prometimos al cliente.
 *
 * 24 horas hábiles, contadas aquí como 24 a secas: la cuenta exacta pediría
 * saber festivos y horarios, y para una bandeja de trabajo lo que hace falta es
 * que lo que lleva demasiado se vea, no que se calcule al minuto.
 */
function isLate(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() > 24 * 60 * 60 * 1000
}

export function AdminAuthCodes({ token }: { token: string }) {
  const { t } = useI18n()
  const [filter, setFilter] = useState<Filter>('pending')
  const [rows, setRows] = useState<AdminAuthCodeRequest[] | null>(null)
  const [failed, setFailed] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  /* Qué fila está pegando su código, y qué lleva escrito. Uno a la vez: son
     credenciales, y dos campos abiertos invitan a pegar una en la fila de otro. */
  const [pasting, setPasting] = useState<string | null>(null)
  const [code, setCode] = useState('')

  const load = useCallback(async () => {
    setFailed('')
    try {
      setRows(await listAuthCodeRequests(token, filter))
    } catch (err) {
      setFailed(translateServerError(err, t('err.loadRequests')))
      setRows([])
    }
  }, [token, filter, t])

  useEffect(() => { void load() }, [load])

  /**
   * Entrega el código y cierra la solicitud.
   *
   * A partir de aquí lo hace todo el servidor: lo guarda, se lo manda al cliente
   * por correo y lo deja visible en los ajustes de su dominio, con la campana
   * encendida. Aquí sólo se pega lo que se sacó del panel del registrador.
   */
  const send = async (id: string) => {
    const value = code.trim()
    if (busy || value.length < 4) return
    setBusy(id)
    setFailed('')
    try {
      await deliverAuthCode(token, id, value)
      setPasting(null)
      setCode('')
      await load()
    } catch (err) {
      setFailed(translateServerError(err, t('err.deliverCode')))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="dash-dashboard-view dash-standard-page">
      <DashboardPageHeader
        title={t('authCodes.title')}
        subtitle={t('authCodes.subtitle')}
      />

      <div className="dash-filter-options dash-authcode-filters">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            className={filter === option.id ? 'active' : ''}
            onClick={() => { setFilter(option.id) }}
          >
            <span>{t(option.labelKey)}</span>
            {filter === option.id ? <IconCheck size={14} /> : null}
          </button>
        ))}
      </div>

      {failed ? (
        <div className="dash-inline-msg">
          <span>{failed}</span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('authCodes.retry')}</button>
          </div>
        </div>
      ) : null}

      {rows === null ? (
        <p className="dash-card-description">{t('authCodes.loading')}</p>
      ) : rows.length === 0 ? (
        <div className="dash-placeholder">
          <IconInbox size={34} aria-hidden="true" />
          <strong>{t(filter === 'pending' ? 'authCodes.emptyPending' : 'authCodes.emptyAll')}</strong>
          <span>
            {t(filter === 'pending' ? 'authCodes.emptyPendingBody' : 'authCodes.emptyAllBody')}
          </span>
        </div>
      ) : (
        <ul className="dash-authcode-list">
          {rows.map((row) => {
            const late = row.status === 'pending' && isLate(row.requestedAt)
            return (
              <li key={row.id} className={`dash-authcode-item${late ? ' is-late' : ''}${pasting === row.id ? ' is-pasting' : ''}`}>
                <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-billing-row-icon is-graphite" aria-hidden="true">
                  <IconWorld size={15} />
                </span>

                <span className="dash-authcode-copy">
                  <strong>
                    {row.hostname}
                    {/* Lo que se ha pasado del plazo se marca. Es una bandeja de
                        trabajo: lo urgente tiene que verse sin leer fechas. */}
                    {late ? <em className="dash-billing-pill is-warn">{t('authCodes.late')}</em> : null}
                    {row.status === 'sent' ? <em className="dash-billing-pill is-ok">{t('authCodes.sent')}</em> : null}
                  </strong>
                  <small>
                    {row.email ?? t('authCodes.unknownAccount')}
                    {row.projectName ? ` · ${row.projectName}` : ''}
                    {' · '}{ago(row.requestedAt)}
                  </small>
                </span>

                <span className="dash-authcode-actions">
                  {row.email ? (
                    <a
                      className="dash-icon-btn"
                      aria-label={t('authCodes.writeTo', { email: row.email })}
                      data-tooltip={t('authCodes.replyByEmail')}
                      href={`mailto:${row.email}?subject=${encodeURIComponent(t('authCodes.mailSubject', { domain: row.hostname }))}`}
                    >
                      <IconMail size={16} />
                    </a>
                  ) : null}

                  {row.status === 'pending' ? (
                    <button
                      type="button"
                      className="dash-primary-btn"
                      disabled={busy !== null}
                      onClick={() => { setPasting(pasting === row.id ? null : row.id); setCode('') }}
                    >
                      <IconClipboard size={14} /> {t('authCodes.paste')}
                    </button>
                  ) : null}
                </span>

                {/* El campo se abre DENTRO de la fila, bajo el dominio del que
                    habla. En un diálogo aparte habría que volver a decir de qué
                    dominio es, y ése es justo el dato que no se puede
                    equivocar: pegar el código de un dominio en la solicitud de
                    otro se lo entrega a quien no es. */}
                {pasting === row.id ? (
                  <form
                    className="dash-authcode-paste"
                    onSubmit={(event) => { event.preventDefault(); void send(row.id) }}
                  >
                    <label>
                      <span className="dash-sr-only">{t('authCodes.srLabel', { domain: row.hostname })}</span>
                      <input
                        autoFocus
                        value={code}
                        placeholder={t('authCodes.placeholder')}
                        spellCheck={false}
                        autoComplete="off"
                        disabled={busy === row.id}
                        onChange={(event) => { setCode(event.target.value) }}
                      />
                    </label>
                    <button type="submit" className="dash-primary-btn" disabled={busy === row.id || code.trim().length < 4}>
                      {busy === row.id
                        ? <><IconLoader2 size={14} className="dash-spin" /> {t('authCodes.sending')}</>
                        : <><IconSend size={14} /> {t('authCodes.sendToClient')}</>}
                    </button>
                    <button
                      type="button"
                      className="dash-authcode-cancel"
                      disabled={busy === row.id}
                      onClick={() => { setPasting(null); setCode('') }}
                    >
                      {t('authCodes.cancel')}
                    </button>
                    <small>{t('authCodes.sendNote')}</small>
                  </form>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      <p className="dash-authcode-note">
        {t('authCodes.note')}
      </p>
    </div>
  )
}
